'use client'
import { memo } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import styled from 'styled-components'
import { AnimatePresence, motion, type Variants } from 'motion/react'
import { getTransition, mq, presets, vw } from '@/styles'
import { LANG } from '@/utils'
import { TypeLang } from '@/schema'

interface IToolTip {
  showTooltip: boolean,
  setShowTooltip?: Dispatch<SetStateAction<boolean>>,
}

const transition = { ...getTransition(.5) } as const

export const listToolTipVariants:Variants = {
  inactive: {
    opacity: 0,
    transition
  },
  enter: {
    opacity: 1,
    transition: {
      ...getTransition(.5),
      staggerChildren: .01
    }
  },
  exit: {
    opacity: 0,
    transition
  }
} as const


const TOOLTIP_TRANSLATIONS:Record<TypeLang, { title: string, description: string }> = {
  'es': {
    'title': 'PRODUCTO SUPERFRESCO',
    'description': 'Hay existencias limitadas de este producto para garantizar que es súper fresco. Recomendamos ordenar con 3 días de anticipación para asegurar disponibilidad. A pedidos urgentes con menos tiempo: haremos todo lo posible para atenderlo en la fecha solicitada o lo antes posible'
  },
  'de': {
    'title': 'SUPERFRISCHES PRODUKT',
    'description': 'Dieses Produkt ist nur begrenzt vorrätig, um sicherzustellen, dass es superfrisch ist. Wir empfehlen, 3 Tage im Voraus zu bestellen, um die Verfügbarkeit zu gewährleisten. Für dringende Bestellungen mit weniger Zeit: Wir werden unser Bestes tun, um sie zum gewünschten Datum oder so schnell wie möglich zu liefern'
  },
  'fr': {
    'title': 'PRODUIT SUPER FRAIS',
    'description': ' There\'s limited stock of this product to warranty it\'s super fresh. We recommend ordering with 3 days in advance to ensure availability. To urgent orders with less time: we\'ll do our best to seve it at the requested date or as soon as possible'
  },
  'en': {
    'title': 'SUPERFRESH PRODUCT',
    'description': 'Il y a un stock limité de ce produit pour garantir qu\'il est super frais. Nous vous recommandons de commander avec 3 jours à l\'avance pour garantir la disponibilité. Pour les commandes urgentes avec moins de temps: nous ferons de notre mieux pour le régler à la date demandée ou dès que possible'
  },
  'pt': {
    'title': 'PRODUTO SUPER FRESCO',
    'description': 'Há estoques limitados deste produto para garantir que ele seja super fresco. Recomendamos fazer o pedido com 3 dias de antecedência para assegurar a disponibilidade. Para pedidos urgentes com menos tempo: faremos o possível para atendê-lo na data solicitada ou o mais breve possível'
  }
}

export const ToolTip = memo<IToolTip>(({ showTooltip }) => {
  return (
    <AnimatePresence mode={'wait'}>
      {showTooltip && (
        <Wrapper className='tooltip' variants={listToolTipVariants} initial='inactive' animate='enter' exit='exit'>
          <p className='title'>{TOOLTIP_TRANSLATIONS[LANG].title}</p>
          <p className='description'>{TOOLTIP_TRANSLATIONS[LANG].description} </p>
        </Wrapper>
      )}
    </AnimatePresence>
  )
})


const Wrapper = styled(motion.div)`
  background-color: var(--color-text--darker);
  height: ${vw(170, 'mobile')};
  left: ${vw(-75, 'mobile')};
  padding: ${vw(21, 'mobile')} ${vw(10, 'mobile')};
  position: relative;
  top: ${vw(10, 'mobile')};
  width: ${vw(170, 'mobile')};

  ${mq.greaterThan('nexus7')} {
    height: ${vw(170, 'tablet')};
    left: ${vw(-75, 'tablet')};
    padding: ${vw(21, 'tablet')} ${vw(10, 'tablet')};
    top: ${vw(10, 'tablet')};
    width: ${vw(170, 'tablet')};
  }

  ${mq.greaterThan('tablet')} {
    height: ${vw(150, 'desktop')};
    left: ${vw(-120, 'desktop')};
    padding: ${vw(21, 'desktop')} ${vw(10, 'desktop')};
    top: ${vw(10, 'desktop')};
    width: ${vw(270, 'desktop')};
  }

  ${mq.greaterThan('desktop')} {
    height: 150px;
    left: -120px;
    padding: 21px 10px;
    top: 10px;
    width: 270px;
  }

   &:after{
    border-bottom-color:  var(--color-text--darker);
    border: ${vw(10, 'mobile')} solid transparent;
    content: "";
    display: block;
    left: 46%;
    position: absolute;
    top: ${vw(-20, 'mobile')};

    ${mq.greaterThan('tablet')} {
      border-bottom-color:  var(--color-text--darker);
      border: ${vw(10, 'desktop')} solid transparent;
      left: 45%;
      top: ${vw(-20, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      border: 10px solid transparent;
      top: 20px;
    }
  }

  p {
    ${presets.font12_1}
    background-color: none;
    color: var(--color-white);
    text-align: center;
  }

  .title {
    padding-bottom: ${vw(10, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      padding-bottom: ${vw(10, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      padding-bottom: ${vw(10, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      padding-bottom: 10px;
    }
  }
`
